import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Card,
  Stack,
  Button,
  Collapse,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
// components
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';
import fmtDuration from '../../utils/formatDuration';

// ---------------------------------------------------------------------------
// Item analysis for ONE quiz.
//
// Reads GET /quiz-attempts/question-analytics, scoped by the same
// course/program/batch as its host report, so the batch report shows how THAT
// cohort answered each question.
// ---------------------------------------------------------------------------

type OptionRow = {
  option: number;
  label: string;
  correct: boolean | null;
  count: number;
  pct: number;
};

type QuestionRow = {
  questionIndex: number;
  questionTitle: string;
  type: string;
  served: number;
  answered: number;
  skipped: number;
  skipRate: number;
  correct: number;
  incorrect: number;
  accuracy: number;
  difficulty: 'easy' | 'medium' | 'hard';
  avgPoints: number;
  maxPoints: number;
  discrimination: number | null;
  misleadingDistractor: boolean;
  deadOptions: number;
  options: OptionRow[];
};

const DIFFICULTY_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  easy: 'success',
  medium: 'warning',
  hard: 'error',
};

// Discrimination reads as a health signal, not a raw number.
function DiscriminationChip({ value }: { value: number | null }) {
  if (value === null) {
    return (
      <Tooltip title="Needs at least 6 attempts to compare top and bottom scorers">
        <span>
          <Label color="default">Discrimination n/a</Label>
        </span>
      </Tooltip>
    );
  }
  const color = value >= 20 ? 'success' : value > 0 ? 'warning' : 'error';
  const hint =
    value >= 20
      ? 'Good — strong students get this right far more often than weak ones.'
      : value > 0
      ? 'Weak — barely separates strong from weak students.'
      : 'Negative — weak students outperform strong ones here. Usually a mis-keyed answer or ambiguous wording.';
  return (
    <Tooltip title={hint}>
      <span>
        <Label color={color}>Discrimination {value > 0 ? `+${value}` : value}</Label>
      </span>
    </Tooltip>
  );
}

export default function QuestionAnalytics({
  quizId,
  courseId,
  eventId,
  batchId,
}: {
  quizId: string;
  courseId?: string;
  eventId?: string;
  batchId?: string;
}) {
  const [rows, setRows] = useState<QuestionRow[]>([]);
  const [weakest, setWeakest] = useState<
    { questionIndex: number; questionTitle: string; accuracy: number }[]
  >([]);
  const [timing, setTiming] = useState({ avg: 0, median: 0, timed: 0 });
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // "Which questions do they fall down on?" — flip to weakest-first.
  const [sortBy, setSortBy] = useState<'order' | 'failed'>('order');
  // Collapsed by default so a long quiz stays scannable; click a row to open.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const toggle = (i: number) => setExpanded((p) => ({ ...p, [i]: !p[i] }));
  const setOpenAll = (open: boolean) =>
    setExpanded(Object.fromEntries(rows.map((r) => [r.questionIndex, open])));

  useEffect(() => {
    if (!quizId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axiosInstance
      .get(APIS.QUIZ_ATTEMPTS.QUESTION_ANALYTICS, {
        params: {
          quizId,
          courseId: courseId || undefined,
          eventId: eventId || undefined,
          batchId: batchId || undefined,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data ?? res.data;
        setRows(Array.isArray(d?.questions) ? d.questions : []);
        setWeakest(Array.isArray(d?.weakest) ? d.weakest : []);
        setTotalAttempts(d?.totalAttempts || 0);
        setTiming({
          avg: d?.avgDurationSeconds || 0,
          median: d?.medianDurationSeconds || 0,
          timed: d?.timedAttempts || 0,
        });
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || 'Failed to load question analytics');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [quizId, courseId, eventId, batchId]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} />
      </Stack>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }
  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No answered questions for this quiz in the current scope yet.
      </Typography>
    );
  }

  const ordered =
    sortBy === 'failed'
      ? [...rows].sort((a, b) => a.accuracy - b.accuracy)
      : [...rows].sort((a, b) => a.questionIndex - b.questionIndex);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="caption" color="text.secondary">
          Based on {totalAttempts} attempt{totalAttempts === 1 ? '' : 's'}. “Answered” excludes
          questions that were served but left blank.
          {timing.avg > 0 && (
            <>
              {' '}
              Whole-quiz time: <b>{fmtDuration(timing.avg)}</b> average,{' '}
              {fmtDuration(timing.median)} median across {timing.timed} timed attempt
              {timing.timed === 1 ? '' : 's'}.
            </>
          )}
        </Typography>
        <TextField
          select
          size="small"
          label="Sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'order' | 'failed')}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="order">Question order</MenuItem>
          <MenuItem value="failed">Most failed first</MenuItem>
        </TextField>
      </Stack>

      {/* The direct answer to "which questions do they fall down on?" */}
      {weakest.length > 0 && (
        <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.neutral' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Weakest questions
          </Typography>
          <Stack spacing={0.5}>
            {weakest.map((w) => (
              <Stack key={w.questionIndex} direction="row" spacing={1} alignItems="center">
                <Label color={w.accuracy >= 50 ? 'warning' : 'error'}>{w.accuracy}%</Label>
                <Typography variant="body2" noWrap>
                  {w.questionIndex + 1}. {w.questionTitle}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button size="small" onClick={() => setOpenAll(true)}>
          Expand all
        </Button>
        <Button size="small" color="inherit" onClick={() => setOpenAll(false)}>
          Collapse all
        </Button>
      </Stack>

      <Stack spacing={2}>
        {ordered.map((q) => {
          const isOpen = expanded[q.questionIndex] ?? false;
          return (
          <Card key={q.questionIndex} variant="outlined" sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'flex-start' }}
              justifyContent="space-between"
              sx={{ mb: 1.5, cursor: 'pointer' }}
              onClick={() => toggle(q.questionIndex)}
            >
              <Box sx={{ flexGrow: 1, pr: 2 }}>
                <Typography variant="subtitle2">
                  {q.questionIndex + 1}. {q.questionTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <b>
                    {q.answered} of {q.served} responded
                  </b>
                  {' · '}
                  {q.correct} correct · {q.incorrect} wrong
                  {q.skipped > 0 ? ` · ${q.skipped} skipped (${q.skipRate}%)` : ''} ·{' '}
                  {q.avgPoints}/{q.maxPoints} avg points
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Label color={DIFFICULTY_COLOR[q.difficulty]}>
                  {q.accuracy}% correct · {q.difficulty}
                </Label>
                <DiscriminationChip value={q.discrimination} />
                <Iconify
                  icon={isOpen ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                  sx={{ color: 'text.secondary' }}
                />
              </Stack>
            </Stack>

            <Collapse in={isOpen} unmountOnExit>
            {/* Flags that mean "this question may be broken", not just hard */}
            {(q.misleadingDistractor || q.deadOptions > 0 || (q.skipRate ?? 0) >= 25) && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                {q.misleadingDistractor && (
                  <Tooltip title="A wrong option was chosen at least as often as the correct one — check the wording or the marked answer.">
                    <span>
                      <Label color="error" startIcon={<Iconify icon="eva:alert-triangle-outline" />}>
                        Misleading distractor
                      </Label>
                    </span>
                  </Tooltip>
                )}
                {q.deadOptions > 0 && (
                  <Tooltip title="Options nobody ever picked — they add no value and make the question easier than intended.">
                    <span>
                      <Label color="default">
                        {q.deadOptions} unused option{q.deadOptions === 1 ? '' : 's'}
                      </Label>
                    </span>
                  </Tooltip>
                )}
                {q.skipRate >= 25 && (
                  <Tooltip title="Often left blank — may be unclear, too long, or placed after a time limit bites.">
                    <span>
                      <Label color="warning">High skip rate</Label>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            )}

            {/* Option distribution */}
            <Stack spacing={1}>
              {q.options.map((o) => (
                <Box key={o.option}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    {o.correct === true && (
                      <Iconify
                        icon="eva:checkmark-circle-2-fill"
                        sx={{ color: 'success.main', width: 16, height: 16 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        fontWeight: o.correct === true ? 600 : 400,
                        color: o.count === 0 ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {o.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.count} ({o.pct}%)
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, o.pct)}
                    color={o.correct === true ? 'success' : o.count > 0 ? 'error' : 'inherit'}
                    sx={{ height: 6, borderRadius: 1 }}
                  />
                </Box>
              ))}
            </Stack>
            </Collapse>
          </Card>
          );
        })}
      </Stack>
    </Box>
  );
}
